import { supabase } from './supabase';
import algoliasearch from 'algoliasearch';

// Initialize Algolia client with environment variables
const algolia = algoliasearch(
  import.meta.env.VITE_ALGOLIA_APP_ID || '',
  import.meta.env.VITE_ALGOLIA_ADMIN_KEY || ''
);

// Initialize separate indexes for different content types
const indexes = {
  blogPosts: algolia.initIndex('blog_posts'),
  courses: algolia.initIndex('courses'),
  nonprofits: algolia.initIndex('nonprofit_resources')
};

// Configure Algolia index settings
async function configureIndexes() {
  try {
    // Validate environment variables
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Missing Algolia credentials - skipping index configuration');
      return;
    }

    // Configure blog posts index
    await indexes.blogPosts.setSettings({
      searchableAttributes: [
        'title',
        'content',
        'excerpt',
        'author',
        'category',
        'tags',
        'location'
      ],
      attributesForFaceting: [
        'category',
        'author',
        'tags',
        'location'
      ],
      customRanking: [
        'desc(published_at)'
      ],
      // Add snippets for voice search results
      attributesToSnippet: [
        'content:150',
        'excerpt:80'
      ],
      // Add synonyms for voice search optimization
      synonyms: [
        {
          objectID: 'non-profit-synonyms',
          type: 'synonym',
          synonyms: ['non-profit', 'nonprofit', 'not-for-profit', 'foundation', 'charity', 'ngo']
        },
        {
          objectID: 'ai-synonyms',
          type: 'synonym',
          synonyms: ['ai', 'artificial intelligence', 'machine learning', 'ml', 'automation']
        },
        {
          objectID: 'midwest-synonyms',
          type: 'synonym',
          synonyms: ['midwest', 'nebraska', 'omaha', 'council bluffs', 'lincoln', 'iowa']
        }
      ]
    });

    // Configure courses index
    await indexes.courses.setSettings({
      searchableAttributes: [
        'title',
        'description',
        'content',
        'level',
        'duration',
        'category',
        'location'
      ],
      attributesForFaceting: [
        'level',
        'category',
        'tags',
        'location'
      ],
      customRanking: [
        'desc(published_at)',
        'desc(is_featured)'
      ]
    });

    // Configure non-profit resources index
    await indexes.nonprofits.setSettings({
      searchableAttributes: [
        'title',
        'description',
        'content',
        'location',
        'organization_type'
      ],
      attributesForFaceting: [
        'location',
        'organization_type',
        'resource_type'
      ],
      customRanking: [
        'desc(published_at)'
      ]
    });
  } catch (error) {
    console.error('Error configuring Algolia indexes:', error);
  }
}

async function syncAllPostsToAlgolia() {
  try {
    console.log('Starting blog posts index sync...');
    
    // Validate environment variables
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Missing Algolia credentials - skipping indexing');
      return false;
    }

    // Configure index settings first
    await configureIndexes();

    const { data: posts, error } = await supabase
      .from('blog_posts')
      .select(`
        *,
        blog_authors (name),
        blog_categories (name),
        blog_post_tags (
          blog_tags (name)
        )
      `)
      .eq('is_published', true)
      .eq('deleted', false);

    if (error) throw error;

    console.log(`Found ${posts?.length || 0} published posts to index`);

    if (!posts || posts.length === 0) {
      console.log('No published posts to index');
      return false;
    }

    // Clear existing index
    await indexes.blogPosts.clearObjects();
    console.log('Cleared existing blog posts index');

    // Prepare posts for Algolia
    const algoliaObjects = posts.map(post => {
      // Determine location context for the post
      let location = 'Global';
      if (post.slug === 'why-non-profits-cant-afford-ignore-ai-any-longer') {
        location = 'Omaha, Nebraska, Midwest';
      }
      
      return {
        objectID: post.id,
        title: post.title,
        content: post.content,
        excerpt: post.excerpt || '',
        slug: post.slug,
        author: post.blog_authors?.name || 'Unknown Author',
        category: post.blog_categories?.name || 'Uncategorized',
        tags: post.blog_post_tags?.map(tag => tag.blog_tags.name) || [],
        published_at: post.published_at,
        url: `https://one80labs.com/blog/${post.slug}`,
        type: 'blog',
        location: location,
        // Add voice search optimized fields
        voiceTitle: `Article about ${post.title}`,
        voiceDescription: `This article discusses ${post.excerpt || post.title}`,
        // Add FAQ data if it's the non-profit post
        faq: post.slug === 'why-non-profits-cant-afford-ignore-ai-any-longer' ? [
          {
            question: "How can small non-profits in Omaha afford to implement AI?",
            answer: "Many AI tools have free or low-cost tiers that are perfect for non-profits with limited budgets."
          },
          {
            question: "What are some simple AI tools that non-profits in Nebraska can start with?",
            answer: "Non-profits in Omaha and throughout the Midwest can start with user-friendly tools like ChatGPT for content creation."
          }
        ] : []
      };
    });

    // Upload to Algolia
    const { objectIDs } = await indexes.blogPosts.saveObjects(algoliaObjects);
    console.log(`Successfully indexed ${objectIDs.length} posts to Algolia`);
    return true;
  } catch (error) {
    console.error('Error syncing posts to Algolia:', error);
    return false;
  }
}

export async function handlePostUpdate(post: any) {
  try {
    // Validate environment variables
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Missing Algolia credentials - skipping post update');
      return;
    }

    if (!post.is_published || post.deleted) {
      // If post is unpublished or deleted, remove it from the index
      await indexes.blogPosts.deleteObject(post.id);
      console.log(`Removed post ${post.id} from search index`);
      return;
    }

    // Determine location context for the post
    let location = 'Global';
    if (post.slug === 'why-non-profits-cant-afford-ignore-ai-any-longer') {
      location = 'Omaha, Nebraska, Midwest';
    }

    // Prepare post data for Algolia
    const algoliaObject = {
      objectID: post.id,
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      slug: post.slug,
      author: post.blog_authors?.name || 'Unknown Author',
      category: post.blog_categories?.name || 'Uncategorized',
      tags: post.blog_post_tags?.map(tag => tag.blog_tags.name) || [],
      published_at: post.published_at,
      url: `https://one80labs.com/blog/${post.slug}`,
      type: 'blog',
      location: location,
      // Add voice search optimized fields
      voiceTitle: `Article about ${post.title}`,
      voiceDescription: `This article discusses ${post.excerpt || post.title}`,
      // Add FAQ data if it's the non-profit post
      faq: post.slug === 'why-non-profits-cant-afford-ignore-ai-any-longer' ? [
        {
          question: "How can small non-profits in Omaha afford to implement AI?",
          answer: "Many AI tools have free or low-cost tiers that are perfect for non-profits with limited budgets."
        },
        {
          question: "What are some simple AI tools that non-profits in Nebraska can start with?",
          answer: "Non-profits in Omaha and throughout the Midwest can start with user-friendly tools like ChatGPT for content creation."
        }
      ] : []
    };

    // Update or add the post in Algolia
    await indexes.blogPosts.saveObject(algoliaObject);
    console.log(`Updated post ${post.id} in search index`);
  } catch (error) {
    console.error('Error updating post in search index:', error);
  }
}

// Function to index non-profit resources
export async function indexNonprofitResources() {
  try {
    // Validate environment variables
    if (!import.meta.env.VITE_ALGOLIA_APP_ID || !import.meta.env.VITE_ALGOLIA_ADMIN_KEY) {
      console.warn('Missing Algolia credentials - skipping indexing');
      return false;
    }

    // Configure index settings first
    await configureIndexes();

    // Static non-profit resources for now
    const resources = [
      {
        objectID: 'nonprofit-ai-ethics-checklist',
        title: 'Non-Profit AI Ethics Checklist',
        description: 'A comprehensive checklist to help non-profits in Omaha and the Midwest implement AI ethically and effectively.',
        content: 'This checklist covers governance, data privacy, fairness, transparency, and implementation strategies for non-profits using AI.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Omaha, Nebraska',
        organization_type: 'non-profit',
        resource_type: 'checklist',
        published_at: new Date().toISOString()
      },
      {
        objectID: 'midwest-nonprofit-ai-guide',
        title: 'AI Implementation Guide for Midwest Non-Profits',
        description: 'A step-by-step guide for non-profits in the Midwest region to implement AI solutions.',
        content: 'This guide provides practical advice for non-profits in Omaha, Lincoln, Council Bluffs, and throughout the Midwest on implementing AI solutions with limited resources.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Midwest',
        organization_type: 'non-profit',
        resource_type: 'guide',
        published_at: new Date().toISOString()
      },
      {
        objectID: 'foundation-ai-tools',
        title: 'AI Tools for Foundations and Grantmakers',
        description: 'A curated list of AI tools specifically useful for foundations and grantmakers in Nebraska.',
        content: 'This resource lists AI tools that can help foundations and grantmakers streamline their operations, improve grant application reviews, and measure impact more effectively.',
        url: '/blog/why-non-profits-cant-afford-ignore-ai-any-longer',
        location: 'Omaha, Nebraska',
        organization_type: 'foundation',
        resource_type: 'tool-list',
        published_at: new Date().toISOString()
      }
    ];

    // Clear existing index
    await indexes.nonprofits.clearObjects();
    console.log('Cleared existing non-profit resources index');

    // Upload to Algolia
    const { objectIDs } = await indexes.nonprofits.saveObjects(resources);
    console.log(`Successfully indexed ${objectIDs.length} non-profit resources to Algolia`);
    return true;
  } catch (error) {
    console.error('Error indexing non-profit resources:', error);
    return false;
  }
}

export { syncAllPostsToAlgolia };